import {
  ACTION_CAPABILITY_LIST,
  ACTION_CAPABILITY_REGISTRY_SCHEMA_VERSION,
  ACTION_CAPABILITY_REGISTRY_SOURCES,
  getActionCapability,
} from '@cogo/action-registry'
import type { FlowMap } from '@cogo/types-v2'
import type {
  StudioActionFlowGenerationAuthority,
  StudioActionFlowGenerationAuthorityIssue,
} from '@/services/block-api-service'
import type { StudioGenerationTargetPlatform } from '@/features/validation/studio-generation-evidence'

type ActionFlowTargetRuntime = 'web' | 'flutter'

interface BuildActionFlowGenerationAuthorityOptions {
  targetPlatform?: StudioGenerationTargetPlatform
}

const NESTED_STEP_FIELDS = [
  'then',
  'else',
  'steps',
  'try',
  'catch',
  'finally',
  'default',
  'onEnter',
  'onSuccess',
  'onError',
  'onFinally',
  'compensate',
  'stages',
] as const

function actionFlowNamesByAction(flows: FlowMap): Map<string, Set<string>> {
  const result = new Map<string, Set<string>>()
  const visit = (flowName: string, candidates: readonly unknown[]) => {
    for (const candidate of candidates) {
      if (
        !candidate ||
        typeof candidate !== 'object' ||
        Array.isArray(candidate)
      )
        continue
      const step = candidate as Record<string, unknown>
      if (typeof step.action === 'string') {
        const flowNames = result.get(step.action) ?? new Set<string>()
        flowNames.add(flowName)
        result.set(step.action, flowNames)
      }
      for (const field of NESTED_STEP_FIELDS) {
        const branch = step[field]
        if (Array.isArray(branch)) visit(flowName, branch)
      }
      if (
        step.cases &&
        typeof step.cases === 'object' &&
        !Array.isArray(step.cases)
      ) {
        for (const branch of Object.values(
          step.cases as Record<string, unknown>,
        )) {
          if (Array.isArray(branch)) visit(flowName, branch)
        }
      }
    }
  }

  for (const [flowName, flow] of Object.entries(flows)) {
    visit(flowName, flow.steps ?? [])
  }
  return result
}

function issueForAction(
  actionName: string,
  flowNames: Set<string>,
  targetRuntime: ActionFlowTargetRuntime,
): StudioActionFlowGenerationAuthorityIssue | null {
  const capability = getActionCapability(actionName)
  const base = { actionName, flowNames: [...flowNames].sort() }
  if (!capability) return { ...base, reason: 'capability-missing' }
  if (capability.lifecycle !== 'active') return { ...base, reason: 'inactive' }
  if (capability.registryExposure !== 'public')
    return { ...base, reason: 'not-public' }
  if (!capability.runtimeImplemented)
    return { ...base, reason: 'runtime-missing' }
  // adapter-only is a legitimate named-flow candidate; P1 target admission
  // decides whether the selected runtime actually provides that adapter.
  if (
    capability.generationPolicy !== 'allow' &&
    capability.generationPolicy !== 'adapter-only'
  )
    return { ...base, reason: 'generation-forbidden' }
  if (capability.targetSupport[targetRuntime] === false)
    return { ...base, reason: 'target-unsupported' }
  return null
}

/**
 * Browser-safe projection of the same generated ActionCapability registry that
 * feeds the prompt-engine ontology shard. Studio plans cannot invent a second
 * action allowlist: every compiled action must be public, active, implemented,
 * and admitted for canonical generation before the request reaches app
 * generation. Target support is evaluated before generation: unsupported
 * actions fail closed, while adapter-backed actions remain named-flow
 * candidates and are surfaced separately for runtime/publish proof.
 */
export function buildActionFlowGenerationAuthority(
  flows: FlowMap | null | undefined,
  options: BuildActionFlowGenerationAuthorityOptions = {},
): StudioActionFlowGenerationAuthority {
  const targetPlatform = options.targetPlatform ?? 'react'
  const targetRuntime: ActionFlowTargetRuntime =
    targetPlatform === 'flutter' ? 'flutter' : 'web'
  const actionFlows = actionFlowNamesByAction(flows ?? {})
  const referencedActionNames = [...actionFlows.keys()].sort()
  const issues = referencedActionNames.flatMap((actionName) => {
    const issue = issueForAction(
      actionName,
      actionFlows.get(actionName)!,
      targetRuntime,
    )
    return issue ? [issue] : []
  })
  const rejected = new Set(issues.map((issue) => issue.actionName))
  const admittedActionNames = referencedActionNames.filter(
    (actionName) => !rejected.has(actionName),
  )
  const adapterRequiredActionNames = admittedActionNames.filter(
    (actionName) =>
      getActionCapability(actionName)?.targetSupport[targetRuntime] === 'adapter',
  )

  return {
    schemaVersion: 'studio-actionflow-generation-authority.v1',
    registrySchemaVersion: ACTION_CAPABILITY_REGISTRY_SCHEMA_VERSION,
    generatedAt: new Date().toISOString(),
    targetPlatform,
    targetRuntime,
    valid: issues.length === 0,
    referencedActionNames,
    admittedActionNames,
    adapterRequiredActionNames,
    directAtomicActionNames: ACTION_CAPABILITY_LIST.filter(
      (capability) =>
        capability.lifecycle === 'active' &&
        capability.registryExposure === 'public' &&
        capability.runtimeImplemented &&
        capability.generationPolicy === 'allow' &&
        capability.targetSupport[targetRuntime] === true &&
        capability.semantics.atomic,
    )
      .map((capability) => capability.name)
      .sort(),
    issues,
    evidenceRefs: [
      'packages/cogo-action-registry/src/generated/action-capability-registry.v1.json',
      'packages/cogo-kg-browser/fixtures/derived/action-capabilities-v1.ttl',
      `target:${targetPlatform}:${targetRuntime}`,
      ...ACTION_CAPABILITY_REGISTRY_SOURCES,
      '@cogo/prompt-engine.loadOntology',
    ],
  }
}
